import React, { useEffect, useMemo, useState } from 'react'
import { Divider, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material'
import * as S from './styles'
import SummaryTableCard from 'src/ui/SummaryTable/SummaryTableCard'
import { BankAccountModel } from 'src/common/api/banking/models'
import { getBanksByIds } from 'src/common/api/banking/data'
import { getEnabledBanks } from 'src/common/api/organizations/data'

const BankDetails: React.FC = () => {
  const [bankAccounts, setBankAccounts] = useState<BankAccountModel[]>([])
  const [selectedBankId, setSelectedBankId] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchBankDetails = async () => {
      try {
        const enabledResponse = await getEnabledBanks()
        const enabledBankIds = enabledResponse.enabledBankIds ?? []

        if (!enabledBankIds.length) {
          setBankAccounts([])
          return
        }

        const idsParam = enabledBankIds.join(',')
        const bankResponse = await getBanksByIds(idsParam)
        setBankAccounts(bankResponse ?? [])
        setSelectedBankId(bankResponse?.[0]?.id ?? '')
      } catch (fetchError) {
        setError('Unable to load bank details. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    fetchBankDetails()
  }, [])

  const selectedBank = useMemo(
    () => bankAccounts.find((bank) => bank.id === selectedBankId),
    [bankAccounts, selectedBankId]
  )

  const summaryRows = useMemo(() => {
    const bank = selectedBank || bankAccounts[0]
    if (!bank) {
      return []
    }

    return Array.isArray(bank.bankDetails)
      ? bank.bankDetails.flatMap((detail) => {
          if (!detail || typeof detail !== 'object') {
            return []
          }

          return Object.entries(detail).map(([section, detailRows]) => {
            const rows = Array.isArray(detailRows)
              ? detailRows.map((row) => ({
                  key: String((row as Record<string, unknown>).key ?? ''),
                  value: String((row as Record<string, unknown>).value ?? '')
                }))
              : detailRows && typeof detailRows === 'object'
              ? Object.entries(detailRows).map(([key, value]) => ({
                  key,
                  value: value == null ? '' : String(value)
                }))
              : [{ key: section, value: String(detailRows ?? '') }]

            return {
              title: section,
              rows
            }
          })
        })
      : []
  }, [bankAccounts, selectedBank])

  return (
    <>
      <S.Flex>
        <Grid container direction="row" spacing={2} sx={{ width: '100%' }}>
          {loading && (
            <Grid item xs={12}>
              <Typography variant="body1">Loading bank payment instructions…</Typography>
            </Grid>
          )}

          {error && (
            <Grid item xs={12}>
              <Typography variant="body1" color="error">
                {error}
              </Typography>
            </Grid>
          )}

          {!loading && !error && bankAccounts.length > 0 && (
            <Grid item xs={12} sx={{ marginBottom: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="bank-select-label">Choose Bank</InputLabel>
                <Select
                  labelId="bank-select-label"
                  value={selectedBankId}
                  label="Choose Bank"
                  onChange={(event: SelectChangeEvent<string>) => setSelectedBankId(event.target.value)}
                >
                  {bankAccounts.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {`${bank.name} - ${bank.externalId}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {!loading && !error && summaryRows.length === 0 && (
            <Grid item xs={12}>
              <Typography variant="body1">No bank payment instructions are available.</Typography>
            </Grid>
          )}

          {!loading &&
            !error &&
            summaryRows.length > 0 &&
            summaryRows.map((bankSection) => (
              <Grid item xs={12} md={6} key={bankSection.title}>
                <SummaryTableCard title={bankSection.title} data={bankSection.rows} />
              </Grid>
            ))}
        </Grid>
      </S.Flex>
      <Divider />
    </>
  )
}

export default BankDetails
